import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Star, Sparkles, Check } from "lucide-react";
import {
  CLIENT_LOGOS, PROBLEMS, BENEFITS, SERVICES, WHY_US, PROCESS_STEPS,
  TESTIMONIALS, PRICING_TIERS, FAQS, STATS,
} from "../lib/data";
import { fadeUp, stagger, staggerChild } from "../lib/motion";
import AnimatedCounter from "../components/AnimatedCounter";
import CTASection from "../components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} aria-hidden="true" />;
};

export default function Home() {
  const featured = ["whitmore-dental", "ironmark-trades", "fernrock-coffee"];
  const featuredProjects = featured.map((s) => require("../lib/data").PORTFOLIO.find((p) => p.slug === s));

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28" data-testid="home-hero">
        <div className="absolute inset-0 subtle-radial" aria-hidden="true" />
        <div className="absolute inset-0 grid-lines-bg opacity-50" aria-hidden="true" />
        <div className="container-page relative">
          <motion.div {...fadeUp} className="max-w-4xl">
            <h1 className="mt-6 text-5xl md:text-7xl lg:text-[88px] font-extrabold tracking-[-0.03em] leading-[1.02] text-slate-900 dark:text-white">
              Smart websites.<br />
              <span className="text-blue-600 dark:text-blue-400">Grow businesses.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
              We build clean, effective websites for small businesses — no jargon, no bloated agency fees, just a site that works as hard as you do. Turning visitors into customers, so you can spend less time worrying about your online presence, and more time running your business.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/pricing" className="btn-primary" data-testid="hero-primary-cta">
                Get an instant quote
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/portfolio" className="btn-secondary" data-testid="hero-secondary-cta">
                View our work
              </Link>
            </div>
            {/* <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-2" data-testid="hero-trust-1">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <img key={i} src={`https://i.pravatar.cc/64?img=${i+10}`} alt="" className="h-7 w-7 rounded-full border-2 border-white dark:border-slate-950" />
                  ))}
                </div>
                <span>Trusted by 120+ businesses</span>
              </div>
              <div className="flex items-center gap-2" data-testid="hero-trust-2">
                <div className="flex text-amber-400">
                  {[1,2,3,4,5].map(i => <Star key={i} className="h-4 w-4 fill-amber-400" />)}
                </div>
                <span>4.9 average rating</span>
              </div>
            </div> */}
          </motion.div>

          {/* Hero visual */}
          
            
        </div>
      </section>

      {/* STATS */}
      <section className="section bg-slate-950 text-white" data-testid="home-stats">
        <div className="container-page">
          <div className="grid gap-10 md:grid-cols-4">
            {STATS.map((s) => (
              <motion.div key={s.label} {...fadeUp} className="border-l border-slate-800 pl-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-5xl md:text-6xl font-extrabold tracking-tighter">
                  <AnimatedCounter value={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-3 text-sm text-slate-400">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* PROBLEM */}
      <section className="section bg-white dark:bg-slate-950 border-y border-slate-100 dark:border-slate-900" data-testid="home-problem">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="eyebrow">The quiet problems</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              You didn't start your business to babysit a website.
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              A recent study found the 41% of customers will trust a business with a modern website over an outdated site, or a business without.
            </p>
          </motion.div>
          <motion.div {...stagger(0.08)} className="mt-16 grid gap-6 md:grid-cols-2">
            {PROBLEMS.map((p, i) => (
              <motion.div key={p.title} {...staggerChild} className="card-surface card-hover p-8" data-testid={`problem-card-${i}`}>
                <div className="flex items-start gap-4">
                  <span className="text-3xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">0{i + 1}</span>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{p.title}</h3>
                    <p className="mt-2 text-slate-600 dark:text-slate-400 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <section className="pt-20 pb-16 md:pt-28 md:pb-24 bg-slate-50 dark:bg-slate-900/40" data-testid="home-clients">
        <div className="container-page">
          <p className="text-center text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 dark:text-slate-500">
            What we offer
          </p>
          <div className="mt-8 overflow-hidden">
            <div className="marquee-track flex gap-16 whitespace-nowrap w-max">
              {/* {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((name, i) => (
                <div
                  key={i}
                  className="text-xl md:text-2xl font-semibold tracking-tight text-slate-400 dark:text-slate-500 opacity-80"
                >
                  {name}
                </div>
              ))} */}
              {SERVICES.map((s) => (
                <div
                  key={s.title}
                  className="text-xl md:text-2xl font-semibold tracking-tight text-slate-400 dark:text-slate-500 opacity-80"
                >
                  {s.title}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS / WHY US */}
      <section className="section bg-white dark:bg-slate-950 border-y border-slate-100 dark:border-slate-900" data-testid="home-benefits">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="eyebrow">Why SolkSites</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              Premium work, without the agency drama.
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              One dedicated developer. A defined process. Clear pricing. And the kind of care that comes from being genuinely invested in every single project.
            </p>
          </motion.div>
          <motion.div {...stagger(0.06)} className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <motion.div
                key={b.title}
                {...staggerChild}
                className="card-surface card-hover p-7 group"
                data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="h-11 w-11 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform">
                  <Icon name={b.icon} className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-900 dark:text-white">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      {/* <section className="section" data-testid="home-services">
        <div className="container-page">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">What we do</p>
              <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                Websites, AI, automation — under one calm roof.
              </h2>
            </div>
            <Link to="/services" className="btn-ghost self-start md:self-auto" data-testid="services-see-all">
              See all services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div {...stagger(0.05)} className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s) => (
              <motion.div key={s.slug} {...staggerChild}>
                <Link
                  to={`/services/${s.slug}`}
                  className="card-surface card-hover p-8 block group h-full"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="h-12 w-12 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-slate-900 dark:text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.short}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 dark:text-blue-400 group-hover:gap-2 transition-all">
                    Explore <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* WHY CHOOSE US */}
      <section className="section bg-slate-50 dark:bg-slate-900/40 border-y border-slate-100 dark:border-slate-900" data-testid="home-why-us">
        <div className="container-page grid lg:grid-cols-12 gap-16">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="eyebrow">Our promise</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              Six things you can count on.
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              We're small on purpose. That means senior work on every project, and a person who actually replies when you email.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400" data-testid="home-about-link">
              Read about us <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div {...stagger(0.06)} className="lg:col-span-7 grid gap-6 sm:grid-cols-2">
            {WHY_US.map((w) => (
              <motion.div key={w.title} {...staggerChild} className="flex gap-4">
                <div className="flex-none h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Icon name={w.icon} className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{w.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{w.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FEATURED WORK */}
      {/* <section className="section" data-testid="home-portfolio">
        <div className="container-page">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Recent work</p>
              <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                A few we're quietly proud of.
              </h2>
            </div>
            <Link to="/portfolio" className="btn-ghost self-start md:self-auto" data-testid="portfolio-see-all">
              See full portfolio <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div {...stagger(0.08)} className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featuredProjects.filter(Boolean).map((p) => (
              <motion.div key={p.slug} {...staggerChild}>
                <Link to={`/portfolio/${p.slug}`} className="group block" data-testid={`portfolio-card-${p.slug}`}>
                  <div className="aspect-[4/3] rounded-[20px] overflow-hidden border border-slate-100 dark:border-slate-800 relative">
                    <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent" />
                    <span className="absolute top-4 left-4 badge-pill bg-white/90 dark:bg-slate-900/90 text-slate-700 dark:text-slate-200 ring-slate-200/70">
                      {p.category}
                    </span>
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">{p.title}</h3>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{p.intro}</p>
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-blue-600 transition-colors flex-none mt-1" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* TESTIMONIALS */}
      {/* <section className="section bg-slate-50 dark:bg-slate-950/50 border-y border-slate-100 dark:border-slate-900" data-testid="home-testimonials">
        <div className="container-page">
          <motion.div {...fadeUp}>
            <p className="eyebrow">Kind words</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white max-w-3xl">
              The best marketing we have is the way our clients talk about us.
            </h2>
          </motion.div>
          <motion.div {...stagger(0.08)} className="mt-14 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure key={i} {...staggerChild} className="card-surface p-8" data-testid={`testimonial-${i}`}>
                <div className="flex text-amber-400 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 fill-amber-400" />)}
                </div>
                <blockquote className="text-lg md:text-xl leading-relaxed text-slate-800 dark:text-slate-100 font-medium">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">{t.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t.role}</p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* PROCESS */}
      <section className="section bg-white dark:bg-slate-950" data-testid="home-process">
        <div className="container-page">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="eyebrow">How we work</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              A calm, predictable process.
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Six clear steps. Fixed price, fixed scope, and a launch date on the calendar from day one.
            </p>
          </motion.div>
          <motion.div {...stagger(0.08)} className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PROCESS_STEPS.map((step) => (
              <motion.div key={step.step} {...staggerChild} className="card-surface p-8" data-testid={`process-step-${step.step}`}>
                <span className="text-sm font-semibold tracking-[0.18em] text-blue-600 dark:text-blue-400">STEP {step.step}</span>
                <h3 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PRICING TEASER */}
      {/* <section className="section bg-white dark:bg-slate-950 border-y border-slate-100 dark:border-slate-900" data-testid="home-pricing">
        <div className="container-page">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Simple pricing</p>
              <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
                Clear numbers. No surprises.
              </h2>
            </div>
            <Link to="/pricing" className="btn-ghost self-start md:self-auto" data-testid="pricing-see-all">
              See full pricing <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div {...stagger(0.06)} className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PRICING_TIERS.map((tier) => (
              <motion.div
                key={tier.name}
                {...staggerChild}
                className={`relative rounded-[20px] p-7 border transition-all ${
                  tier.highlight
                    ? "bg-slate-950 text-white border-slate-900"
                    : "bg-white dark:bg-slate-900/40 border-slate-100 dark:border-slate-800"
                }`}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3 left-6 badge-pill bg-blue-500 text-white ring-blue-400/50">Most popular</span>
                )}
                <h3 className={`text-lg font-semibold ${tier.highlight ? "text-white" : "text-slate-900 dark:text-white"}`}>{tier.name}</h3>
                <p className={`mt-1 text-sm ${tier.highlight ? "text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>{tier.tagline}</p>
                <p className={`mt-5 text-4xl font-extrabold tracking-tighter ${tier.highlight ? "text-white" : "text-slate-900 dark:text-white"}`}>{tier.price}</p>
                <p className={`text-xs ${tier.highlight ? "text-slate-400" : "text-slate-500"}`}>{tier.cadence}</p>
                <ul className={`mt-6 space-y-2.5 text-sm ${tier.highlight ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                  {tier.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className={`h-4 w-4 mt-0.5 flex-none ${tier.highlight ? "text-blue-400" : "text-blue-600"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-5 py-3 text-sm font-semibold transition-all ${
                    tier.highlight
                      ? "bg-white text-slate-900 hover:bg-slate-100"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {tier.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* FAQ */}
      <section className="section bg-slate-50 dark:bg-slate-900/40" data-testid="home-faq">
        <div className="container-page grid lg:grid-cols-12 gap-16">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="eyebrow">Questions</p>
            <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white">
              Everything you might be wondering.
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Can't find the answer? Ask us directly — we usually reply the same day.
            </p>
            <Link to="/faq" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400" data-testid="home-faq-see-all">
              Full FAQ <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.slice(0, 6).map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="card-surface px-6 border data-[state=open]:border-blue-200 dark:data-[state=open]:border-blue-500/40"
                >
                  <AccordionTrigger className="text-left py-5 hover:no-underline font-semibold text-base text-slate-900 dark:text-white" data-testid={`faq-item-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 dark:text-slate-400 pb-5 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
